import { Loading } from "../components/Loading";
import { useQuery } from "@tanstack/react-query";
import { Get } from "../api/api";
import { DEVICE_SHOP_ID_KEY } from "../api/device";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Icon } from "@iconify/react";
import { Navigate, useNavigate } from "react-router";
import { useContext, useState } from "react";
import { UserContext } from "../context/user";

const STEPS = [
  { label: "Tienda", icon: "mdi:store" },
  { label: "Ejercicio", icon: "mdi:calendar-check" },
];

function StepIndicator({ current }) {
  return (
    <div className="flex items-center justify-center gap-3 mb-8">
      {STEPS.map((step, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <div key={step.label} className="flex items-center gap-3">
            {i > 0 && (
              <div
                className={`w-12 h-0.5 rounded ${done ? "bg-blue-500" : "bg-neutral-200"}`}
              />
            )}
            <div className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                  done
                    ? "bg-blue-500 text-white"
                    : active
                      ? "bg-blue-100 text-blue-700 ring-2 ring-blue-500"
                      : "bg-neutral-100 text-neutral-400"
                }`}
              >
                {done ? (
                  <Icon icon="mdi:check" className="text-base" />
                ) : (
                  i + 1
                )}
              </div>
              <span
                className={`text-sm font-medium ${active ? "text-neutral-800" : "text-neutral-400"}`}
              >
                {step.label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function SelectShop() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [selectedShop, setSelectedShop] = useState(null);
  const currentShopId = localStorage.getItem("shop_id");
  const boundDeviceShopId = localStorage.getItem(DEVICE_SHOP_ID_KEY);
  const isDeviceLocked = Boolean(boundDeviceShopId && boundDeviceShopId === currentShopId);

  const { data: shops, isLoading: shopsLoading } = useQuery({
    queryKey: ["shops", localStorage.getItem("token")],
    queryFn: () => Get({ request: "/shops" }),
    enabled: !isDeviceLocked,
  });

  const { data: exercises, isLoading: exercisesLoading } = useQuery({
    queryKey: ["exercises", selectedShop?.id],
    queryFn: () =>
      Get({ request: `/exercises?shop_id=${selectedShop.id}` }),
    enabled: !!selectedShop,
  });

  const availableShops = isDeviceLocked
    ? (Array.isArray(user.shops) ? user.shops : [])
    : (Array.isArray(shops) ? shops : []);

  if (shops?.code == 401) return <Navigate to="/login" />;
  if (shopsLoading) return <Loading />;

  const handleSelectShop = (shop) => {
    setSelectedShop(shop);
    setStep(1);
  };

  const handleSelectExercise = (exercise) => {
    localStorage.setItem("shop_id", selectedShop.id);
    localStorage.setItem("exercise_id", exercise.id);
    navigate("/");
  };

  const handleBack = () => {
    setStep(0);
    setSelectedShop(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("shop_id");
    localStorage.removeItem("exercise_id");
    navigate("/login");
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center select-none bg-neutral-50">
      <div className="w-full max-w-lg px-6">
        {/* Header */}
        <div className="text-center mb-6">
          <p className="text-lg text-neutral-600">
            Hola, <span className="font-semibold">{user.name}</span>
          </p>
        </div>

        <StepIndicator current={step} />

        {/* Step 0: Shops */}
        {step === 0 && (
          <div className="animate-fade animate-once">
            {availableShops.length === 0 ? (
              <Card className="max-w-sm mx-auto">
                <CardContent className="p-6 text-center text-sm text-neutral-500">
                  No hay ninguna tienda disponible para esta sesión.
                </CardContent>
              </Card>
            ) : availableShops.length === 1 ? (
              <>
                <p className="text-center text-sm text-neutral-500 mb-5">
                  ¿Quieres acceder a esta tienda?
                </p>
                <Card className="max-w-sm mx-auto ring-2 ring-blue-400">
                  <CardContent className="p-6 flex flex-col items-center text-center gap-3">
                    <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center">
                      <Icon
                        icon="mdi:store"
                        className="text-2xl text-blue-600"
                      />
                    </div>
                    <div>
                      <div className="text-lg font-bold">{availableShops[0].name}</div>
                      {availableShops[0].address && (
                        <div className="text-sm text-neutral-500 mt-0.5">
                          {availableShops[0].address}
                        </div>
                      )}
                    </div>
                    {(availableShops[0].fiscal_name || availableShops[0].fiscal_id) && (
                      <div className="w-full border-t pt-3 mt-1 space-y-1">
                        {availableShops[0].fiscal_name && (
                          <div className="text-xs text-neutral-400">
                            {availableShops[0].fiscal_name}
                          </div>
                        )}
                        {availableShops[0].fiscal_id && (
                          <div className="text-xs text-neutral-400">
                            CIF: {availableShops[0].fiscal_id}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
                <div className="flex justify-center mt-6">
                  <Button
                    className="h-12 px-8 text-base"
                    onClick={() => handleSelectShop(availableShops[0])}
                  >
                    Siguiente
                    <Icon icon="mdi:arrow-right" className="ml-2" />
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-center text-sm text-neutral-500 mb-5">
                  Selecciona la tienda con la que vas a trabajar
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {availableShops.map((shop) => (
                    <Card
                      key={shop.id}
                      className="cursor-pointer hover:ring-2 hover:ring-blue-400 active:scale-[0.97] transition-all"
                      onClick={() => handleSelectShop(shop)}
                    >
                      <CardContent className="p-5 flex items-center gap-4">
                        <div className="shrink-0 w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center">
                          <Icon
                            icon="mdi:store"
                            className="text-xl text-blue-600"
                          />
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold truncate">
                            {shop.name}
                          </div>
                          {shop.address && (
                            <div className="text-xs text-neutral-400 truncate">
                              {shop.address}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}

            <div className="flex justify-center mt-8">
              <Button
                variant="ghost"
                size="sm"
                className="text-neutral-400"
                onClick={handleLogout}
              >
                <Icon icon="mdi:logout" className="mr-1.5" />
                Cerrar sesión
              </Button>
            </div>
          </div>
        )}

        {/* Step 1: Exercises */}
        {step === 1 && (
          <div className="animate-fade animate-once">
            <p className="text-center text-sm text-neutral-500 mb-1">
              <span className="font-medium text-neutral-700">
                {selectedShop?.name}
              </span>
            </p>
            <p className="text-center text-sm text-neutral-500 mb-5">
              Selecciona el ejercicio fiscal
            </p>

            {exercisesLoading ? (
              <div className="flex justify-center py-12">
                <Icon
                  icon="eos-icons:three-dots-loading"
                  className="text-4xl text-neutral-400"
                />
              </div>
            ) : (
              <div className="space-y-2">
                {exercises?.map((exercise) => (
                  <Card
                    key={exercise.id}
                    className="cursor-pointer hover:ring-2 hover:ring-blue-400 active:scale-[0.97] transition-all"
                    onClick={() => handleSelectExercise(exercise)}
                  >
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="shrink-0 w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center">
                        <Icon
                          icon="mdi:calendar-check"
                          className="text-xl text-emerald-600"
                        />
                      </div>
                      <div className="font-semibold">{exercise.name}</div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            <div className="flex justify-center mt-6">
              <Button variant="ghost" size="sm" onClick={handleBack}>
                <Icon icon="mdi:arrow-left" className="mr-1.5" />
                Volver
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
